// Prozedurales Audio (WebAudio): Atmosphäre je Szene, Musik, Effekte – alles synthetisiert, keine Audiodateien.
// Drei getrennte Busse mit eigenen Lautstärken; Untertitel für Geräusche über onCaption.

export type Bus = 'music' | 'ambience' | 'sfx';
export interface AudioSettings {
  master: number;
  music: number;
  ambience: number;
  sfx: number;
}

type Caption = (text: string) => void;

const NOTE: Record<string, number> = { C: 0, 'C#': 1, D: 2, 'D#': 3, E: 4, F: 5, 'F#': 6, G: 7, 'G#': 8, A: 9, 'A#': 10, B: 11 };
export function midi(n: string): number {
  const m = /^([A-G]#?)(-?\d)$/.exec(n);
  if (!m) return 60;
  return NOTE[m[1]] + (Number(m[2]) + 1) * 12;
}
export const hz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

class AudioEngine {
  ctx: AudioContext | null = null;
  private master!: GainNode;
  private buses = {} as Record<Bus, GainNode>;
  private noiseBuf: AudioBuffer | null = null;
  settings: AudioSettings = { master: 0.8, music: 0.45, ambience: 0.6, sfx: 0.8 };
  onCaption: Caption = () => {};
  private ambienceStop: (() => void) | null = null;
  private musicStop: (() => void) | null = null;
  private currentAmbience = '';
  private currentMusic = '';
  muted = false;

  constructor() {
    try {
      const s = localStorage.getItem('dlr.audio');
      if (s) this.settings = { ...this.settings, ...JSON.parse(s) };
    } catch {
      /* egal */
    }
  }

  /** muss nach einer Nutzergeste aufgerufen werden */
  unlock() {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return;
    }
    const AC = window.AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    this.master = this.ctx.createGain();
    // sanfter Kompressor gegen Spitzen
    const comp = this.ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 3;
    this.master.connect(comp).connect(this.ctx.destination);
    for (const b of ['music', 'ambience', 'sfx'] as Bus[]) {
      this.buses[b] = this.ctx.createGain();
      this.buses[b].connect(this.master);
    }
    this.applySettings();
    // Noise-Puffer (2 s, braun-rosa gemischt)
    const len = this.ctx.sampleRate * 2;
    this.noiseBuf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = this.noiseBuf.getChannelData(0);
    let last = 0;
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1;
      last = (last + 0.02 * w) / 1.02;
      d[i] = last * 3.5 + w * 0.15;
    }
    if (this.pendingAmbience) this.ambience(this.pendingAmbience);
    if (this.pendingMusic) this.music(this.pendingMusic);
  }

  applySettings() {
    try {
      localStorage.setItem('dlr.audio', JSON.stringify(this.settings));
    } catch {
      /* egal */
    }
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.master.gain.setTargetAtTime(this.muted ? 0 : this.settings.master, t, 0.05);
    for (const b of ['music', 'ambience', 'sfx'] as Bus[]) this.buses[b].gain.setTargetAtTime(this.settings[b], t, 0.05);
  }

  setVolume(b: Bus | 'master', v: number) {
    this.settings[b] = Math.max(0, Math.min(1, v));
    this.applySettings();
  }

  // ---------- Bausteine ----------

  private noise(bus: Bus, opts: { type?: BiquadFilterType; freq?: number; q?: number; gain?: number; loop?: boolean; dur?: number; at?: number }) {
    const c = this.ctx!;
    const src = c.createBufferSource();
    src.buffer = this.noiseBuf;
    src.loop = opts.loop ?? true;
    src.playbackRate.value = 0.8 + Math.random() * 0.4;
    const f = c.createBiquadFilter();
    f.type = opts.type ?? 'lowpass';
    f.frequency.value = opts.freq ?? 800;
    f.Q.value = opts.q ?? 0.7;
    const g = c.createGain();
    g.gain.value = opts.gain ?? 0.2;
    src.connect(f).connect(g).connect(this.buses[bus]);
    const at = opts.at ?? c.currentTime;
    src.start(at, Math.random());
    if (opts.dur) src.stop(at + opts.dur);
    return { src, f, g };
  }

  private tone(bus: Bus, freq: number, at: number, dur: number, opts: { type?: OscillatorType; gain?: number; attack?: number; release?: number; detune?: number; filter?: number; vibrato?: number } = {}) {
    const c = this.ctx!;
    const o = c.createOscillator();
    o.type = opts.type ?? 'sine';
    o.frequency.value = freq;
    if (opts.detune) o.detune.value = opts.detune;
    const g = c.createGain();
    const a = opts.attack ?? 0.01, r = opts.release ?? 0.3, peak = opts.gain ?? 0.2;
    g.gain.setValueAtTime(0, at);
    g.gain.linearRampToValueAtTime(peak, at + a);
    g.gain.setValueAtTime(peak, at + Math.max(a, dur - r));
    g.gain.linearRampToValueAtTime(0, at + dur);
    let node: AudioNode = o;
    if (opts.filter) {
      const f = c.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = opts.filter;
      node.connect(f);
      node = f;
    }
    if (opts.vibrato) {
      const l = c.createOscillator();
      l.frequency.value = 5.2;
      const lg = c.createGain();
      lg.gain.value = opts.vibrato;
      l.connect(lg).connect(o.frequency);
      l.start(at);
      l.stop(at + dur);
    }
    node.connect(g).connect(this.buses[bus]);
    o.start(at);
    o.stop(at + dur + 0.05);
    return o;
  }

  // ---------- Effekte ----------

  sfx(name: string) {
    const caption = SFX_CAPTIONS[name];
    if (caption) this.onCaption(caption);
    if (!this.ctx || !this.noiseBuf) return;
    const c = this.ctx, t = c.currentTime;
    switch (name) {
      case 'bell': {
        // Glocke: inharmonische Teiltöne
        for (const [ratio, g, d] of [[1, 0.35, 3.5], [2.0, 0.18, 2.6], [2.76, 0.12, 2], [5.4, 0.06, 1.2], [8.9, 0.03, 0.8]]) {
          for (let k = 0; k < 3; k++) this.tone('sfx', 660 * ratio, t + k * 0.45, d, { gain: g * (1 - k * 0.15), attack: 0.002, release: d * 0.95 });
        }
        break;
      }
      case 'unlock':
        this.noise('sfx', { type: 'bandpass', freq: 2500, q: 4, gain: 0.5, loop: false, dur: 0.05 });
        this.noise('sfx', { type: 'bandpass', freq: 1800, q: 4, gain: 0.5, loop: false, dur: 0.06, at: t + 0.12 });
        this.tone('sfx', 220, t + 0.12, 0.25, { type: 'triangle', gain: 0.15 });
        break;
      case 'pickup':
        this.tone('sfx', hz(76), t, 0.18, { type: 'triangle', gain: 0.18 });
        this.tone('sfx', hz(83), t + 0.08, 0.25, { type: 'triangle', gain: 0.15 });
        break;
      case 'door':
        this.noise('sfx', { type: 'bandpass', freq: 400, q: 8, gain: 0.6, loop: false, dur: 0.7 });
        this.tone('sfx', 90, t + 0.5, 0.4, { type: 'triangle', gain: 0.25, filter: 300 });
        break;
      case 'right':
        [72, 76, 79, 84].forEach((m, i) => this.tone('sfx', hz(m), t + i * 0.08, 0.5, { type: 'triangle', gain: 0.16, release: 0.4 }));
        break;
      case 'wrong':
        this.tone('sfx', hz(52), t, 0.5, { type: 'sine', gain: 0.22, release: 0.4 });
        this.tone('sfx', hz(51), t + 0.15, 0.6, { type: 'sine', gain: 0.18, release: 0.5 });
        break;
      case 'page':
        this.noise('sfx', { type: 'highpass', freq: 3000, gain: 0.25, loop: false, dur: 0.25 });
        break;
      case 'combine':
        this.tone('sfx', hz(67), t, 0.15, { type: 'square', gain: 0.06, filter: 2000 });
        this.tone('sfx', hz(74), t + 0.1, 0.3, { type: 'triangle', gain: 0.15 });
        break;
      case 'applause':
        for (let i = 0; i < 40; i++) this.noise('sfx', { type: 'bandpass', freq: 1500 + Math.random() * 2000, q: 2, gain: 0.15, loop: false, dur: 0.04, at: t + Math.random() * 2.5 });
        break;
      case 'drumroll':
        for (let i = 0; i < 30; i++) this.noise('sfx', { type: 'bandpass', freq: 200, q: 1, gain: 0.25 + i * 0.01, loop: false, dur: 0.05, at: t + i * 0.05 });
        break;
      case 'gull':
        this.gull(t);
        break;
    }
  }

  private gull(t: number) {
    const c = this.ctx!;
    for (let k = 0; k < 3; k++) {
      const o = c.createOscillator();
      o.type = 'sawtooth';
      const g = c.createGain();
      const f = c.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = 1800;
      f.Q.value = 3;
      const at = t + k * 0.32;
      o.frequency.setValueAtTime(1300, at);
      o.frequency.exponentialRampToValueAtTime(2100, at + 0.08);
      o.frequency.exponentialRampToValueAtTime(900, at + 0.28);
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(0.08, at + 0.03);
      g.gain.linearRampToValueAtTime(0, at + 0.28);
      o.connect(f).connect(g).connect(this.buses.ambience);
      o.start(at);
      o.stop(at + 0.3);
    }
  }

  /** spielt eine Melodie (für Audiofragen): [Note, Dauer in Schlägen][] */
  melody(notes: [string, number][], bpm = 100, bus: Bus = 'sfx'): number {
    if (!this.ctx) return 0;
    const beat = 60 / bpm;
    let t = this.ctx.currentTime + 0.1;
    for (const [n, d] of notes) {
      if (n !== 'R') {
        const f = hz(midi(n));
        this.tone(bus, f, t, d * beat * 0.95, { type: 'triangle', gain: 0.22, release: 0.12, vibrato: 3 });
        this.tone(bus, f * 2, t, d * beat * 0.9, { type: 'sine', gain: 0.05, release: 0.1 });
      }
      t += d * beat;
    }
    return (t - this.ctx.currentTime) * 1000;
  }

  /** Morsezeichen (für Audiofragen) */
  morse(code: string, wpm = 14): number {
    if (!this.ctx) return 0;
    const unit = 1.2 / wpm;
    let t = this.ctx.currentTime + 0.1;
    for (const ch of code) {
      if (ch === '.' || ch === '-') {
        const d = ch === '.' ? unit : unit * 3;
        this.tone('sfx', 700, t, d, { type: 'sine', gain: 0.25, attack: 0.005, release: 0.01 });
        t += d + unit;
      } else if (ch === ' ') t += unit * 2;
      else if (ch === '/') t += unit * 6;
    }
    return (t - this.ctx.currentTime) * 1000;
  }

  // ---------- Atmosphäre ----------

  private pendingAmbience = '';
  ambience(name: string) {
    this.pendingAmbience = name;
    if (!this.ctx || !this.noiseBuf || name === this.currentAmbience) return;
    this.ambienceStop?.();
    this.currentAmbience = name;
    const c = this.ctx;
    const stops: (() => void)[] = [];
    const timers: number[] = [];
    const every = (ms: number, jitter: number, fn: () => void) => {
      const run = () => {
        fn();
        timers.push(window.setTimeout(run, ms + Math.random() * jitter));
      };
      timers.push(window.setTimeout(run, Math.random() * ms));
    };
    const lfo = (param: AudioParam, rate: number, depth: number) => {
      const l = c.createOscillator();
      l.frequency.value = rate;
      const g = c.createGain();
      g.gain.value = depth;
      l.connect(g).connect(param);
      l.start();
      stops.push(() => l.stop());
    };
    if (name === 'pub') {
      // Ofen-Rauschen, Feuerknacken, Uhr, fernes Gemurmel
      const fire = this.noise('ambience', { type: 'lowpass', freq: 500, gain: 0.08 });
      stops.push(() => fire.src.stop());
      const room = this.noise('ambience', { type: 'bandpass', freq: 350, q: 1.2, gain: 0.05 });
      lfo(room.g.gain, 0.13, 0.03);
      stops.push(() => room.src.stop());
      every(700, 1600, () => this.noise('ambience', { type: 'highpass', freq: 2500, gain: 0.12 + Math.random() * 0.15, loop: false, dur: 0.02 + Math.random() * 0.03 }));
      every(1000, 0, () => this.noise('ambience', { type: 'bandpass', freq: 3500, q: 10, gain: 0.12, loop: false, dur: 0.015 }));
    } else if (name === 'harbor') {
      // Wellen, Wind, Möwen, knarzendes Tau
      const waves = this.noise('ambience', { type: 'lowpass', freq: 600, gain: 0.12 });
      lfo(waves.g.gain, 0.12, 0.09);
      lfo(waves.f.frequency, 0.09, 300);
      stops.push(() => waves.src.stop());
      const wind = this.noise('ambience', { type: 'bandpass', freq: 900, q: 0.8, gain: 0.03 });
      lfo(wind.f.frequency, 0.05, 400);
      stops.push(() => wind.src.stop());
      every(7000, 9000, () => this.gull(c.currentTime));
      every(5000, 6000, () => this.tone('ambience', 120 + Math.random() * 40, c.currentTime, 0.6, { type: 'sawtooth', gain: 0.012, filter: 400 }));
    } else if (name === 'cellar') {
      const hum = this.noise('ambience', { type: 'lowpass', freq: 150, gain: 0.08 });
      stops.push(() => hum.src.stop());
      every(2500, 4000, () => {
        const f = 1200 + Math.random() * 1500;
        this.tone('ambience', f, c.currentTime, 0.12, { type: 'sine', gain: 0.06, attack: 0.002, release: 0.1 });
      });
    } else if (name === 'wind') {
      const wind = this.noise('ambience', { type: 'bandpass', freq: 700, q: 0.6, gain: 0.1 });
      lfo(wind.f.frequency, 0.07, 500);
      lfo(wind.g.gain, 0.11, 0.05);
      stops.push(() => wind.src.stop());
    }
    this.ambienceStop = () => {
      stops.forEach((s) => s());
      timers.forEach((t) => clearTimeout(t));
    };
  }

  // ---------- Musik ----------

  private pendingMusic = '';
  /** ruhige, prozedural erzeugte Folk-/Shanty-Musik; name wählt Tonart/Stimmung */
  /** dynamische Musik: Tageszeit (0 Morgen … 3 Nacht) und Spannung (Rätsel offen) färben jede neue Takt-Gruppe */
  mood = 1;
  tension = false;
  setMood(time: number) {
    this.mood = Math.max(0, Math.min(3, time));
  }
  setTension(on: boolean) {
    this.tension = on;
  }

  music(name: string) {
    this.pendingMusic = name;
    if (!this.ctx || name === this.currentMusic) return;
    this.musicStop?.();
    this.currentMusic = name;
    if (name === 'none') return;
    const preset = MUSIC[name] ?? MUSIC.pub;
    const beat = 60 / preset.bpm;
    let bar = 0;
    let alive = true;
    let seed = preset.seed;
    const r = () => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return seed / 0x7fffffff;
    };
    const scheduleBar = () => {
      if (!alive || !this.ctx) return;
      const t0 = this.ctx.currentTime + 0.1;
      const chord = preset.chords[bar % preset.chords.length];
      const root = midi(chord[0]);
      // Tageszeit: morgens hell und lebhaft, abends wärmer, nachts gedämpft und spärlich
      const m = this.mood;
      const filter = [1900, 1400, 1100, 750][m];
      const melodyDensity = [0.9, 0.85, 0.7, 0.45][m];
      const octave = m === 0 ? 12 : m === 3 ? 0 : 12;
      // Bass (Grundton + Quinte); nachts nur der Grundton
      this.tone('music', hz(root - 12), t0, beat * 1.9, { type: 'triangle', gain: 0.12, release: 0.3 });
      if (m < 3) this.tone('music', hz(root - 5), t0 + beat * 2, beat * 1.9, { type: 'triangle', gain: 0.1, release: 0.3 });
      // Akkordeon-Flächen (zwei verstimmte Sägezähne, gefiltert)
      for (const n of chord) {
        for (const det of [-7, 7]) this.tone('music', hz(midi(n)), t0, beat * 4, { type: 'sawtooth', gain: m === 3 ? 0.014 : 0.018, detune: det, filter, attack: m === 3 ? 0.6 : 0.25, release: 0.6 });
      }
      // Spannung (Rätsel/Quiz offen): leiser Herzschlag auf dem Grundton
      if (this.tension) for (let k = 0; k < 4; k++) this.tone('music', hz(root - 24), t0 + k * beat, beat * 0.25, { type: 'sine', gain: 0.09, release: 0.08 });
      // Melodie aus Akkord- und Skalentönen, ruhig
      if (bar % 8 >= 2 && !(this.tension && bar % 2)) {
        let t = t0;
        const scale = preset.scale.map(midi);
        let idx = Math.floor(r() * scale.length);
        for (let b = 0; b < 4; ) {
          const d = r() < 0.6 ? 1 : r() < 0.5 ? 2 : 0.5;
          if (r() < melodyDensity) {
            idx = Math.max(0, Math.min(scale.length - 1, idx + Math.floor(r() * 5) - 2));
            this.tone('music', hz(scale[idx] + octave), t, d * beat * 0.9, { type: m === 0 ? 'square' : 'triangle', gain: m === 0 ? 0.03 : 0.06, vibrato: 2.5, release: 0.15, filter: m === 0 ? 2400 : undefined });
          }
          t += d * beat;
          b += d;
        }
      }
      bar++;
    };
    scheduleBar();
    const iv = window.setInterval(scheduleBar, beat * 4 * 1000);
    this.musicStop = () => {
      alive = false;
      clearInterval(iv);
    };
  }

  stopAll() {
    this.ambienceStop?.();
    this.musicStop?.();
    this.currentAmbience = '';
    this.currentMusic = '';
  }
}

const MUSIC: Record<string, { bpm: number; seed: number; chords: string[][]; scale: string[] }> = {
  pub: {
    bpm: 84,
    seed: 7,
    chords: [['D4', 'F4', 'A4'], ['A3', 'C#4', 'E4'], ['D4', 'F4', 'A4'], ['G3', 'A#3', 'D4'], ['F4', 'A4', 'C5'], ['C4', 'E4', 'G4'], ['A3', 'C#4', 'E4'], ['D4', 'F4', 'A4']],
    scale: ['D4', 'E4', 'F4', 'G4', 'A4', 'A#4', 'C5', 'D5'],
  },
  harbor: {
    bpm: 72,
    seed: 3,
    chords: [['G3', 'B3', 'D4'], ['E3', 'G3', 'B3'], ['C4', 'E4', 'G4'], ['D4', 'F#4', 'A4']],
    scale: ['G4', 'A4', 'B4', 'D5', 'E5', 'G5'],
  },
  show: {
    bpm: 112,
    seed: 11,
    chords: [['C4', 'E4', 'G4'], ['F4', 'A4', 'C5'], ['G3', 'B3', 'D4'], ['C4', 'E4', 'G4']],
    scale: ['C4', 'D4', 'E4', 'G4', 'A4', 'C5'],
  },
  night: {
    bpm: 60,
    seed: 5,
    chords: [['A3', 'C4', 'E4'], ['F3', 'A3', 'C4'], ['C4', 'E4', 'G4'], ['G3', 'B3', 'D4']],
    scale: ['A4', 'C5', 'D5', 'E5', 'G5'],
  },
};

/** Untertitel für Geräusche (Barrierefreiheit) */
export const SFX_CAPTIONS: Record<string, string> = {
  bell: '[Glocke läutet]',
  unlock: '[Schloss klickt auf]',
  pickup: '[eingesteckt]',
  door: '[Tür knarrt]',
  right: '[fröhlicher Akkord]',
  wrong: '[tiefer Ton]',
  page: '[Papier raschelt]',
  combine: '[es klickt zusammen]',
  applause: '[Applaus]',
  drumroll: '[Trommelwirbel]',
  gull: '[Möwe kreischt]',
};

export const audio = new AudioEngine();
// Test-Schnittstelle: Stimmung der dynamischen Musik ablesen
if (typeof window !== "undefined") (window as any).__dlrAudio = audio;
